import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ClientService } from "@services";
import { ORGANIZATION_TYPE, PROCESSING_FEE_ORIGINS } from "@constants";
import utils from "@utils";

import { Loader, Modal } from "../../../../../components";
import ModifyRate from "../OrganizationFeeModal/ModifyRate";
import "./ClientFeesModal.scss";

const ClientFeesModal = props => {
	const { lang } = global;
	const { show, onHide, client, organizationId, details, type } = props;

	const { LAW_ENFORCEMENT_DEPARTMENT } = ORGANIZATION_TYPE;

	const [showRKRate, setShowRKRate] = useState(false);
	const [serviceFees, setServiceFees] = useState([]);
	const [activeServiceFee, setActiveServiceFee] = useState(null);
	const [processingFees, setProcessingFees] = useState([]);
	const [processingFeesHistory, setProcessingFeesHistory] = useState([]);
	const [activeProcessingFees, setActiveProcessingFees] = useState([]);
	const [loadingProcessingFees, setLoadingProcessingFees] = useState(false);
	const [loadingServiceFees, setLoadingServiceFees] = useState(false);

	const mapFeeObject = (pFees, showFee = false) => ({
		id: pFees.Id,
		effectiveFrom: pFees.EffectiveFrom,
		modifier: pFees.Modifier,
		name: pFees.Name,
		origin: pFees.Origin,
		processingFeeId: pFees.ProcessingFeeId,
		rate: pFees.Rate,
		show: showFee,
	});

	const createProcessingFees = () => {
		if (processingFees.length > 0) {
			const pfeesAux = activeProcessingFees.map(pFee => {
				const pFeeAux = processingFees.find(
					pFees => pFees.processingFeeId === pFee.ProcessingFeeId,
				);
				if (pFeeAux) {
					return mapFeeObject(pFee, pFeeAux.show);
				}
				return mapFeeObject(pFee);
			});
			setProcessingFees(pfeesAux);
		} else {
			const pfeesAux = activeProcessingFees.map(pFee => mapFeeObject(pFee));
			setProcessingFees(pfeesAux);
		}
	};

	const loadClientServiceFees = async () => {
		try {
			setLoadingServiceFees(true);
			const response = await ClientService.getServiceFees(client.Id, { organizationId });
			const serviceFeesData = response.data.map(({ PercentageFee, ...rate }) => ({
				...rate,
				Rate: PercentageFee,
			}));
			setServiceFees(serviceFeesData);
			const currentdate = new Date();
			const effectiveServiceFees = serviceFeesData.filter(
				fee => new Date(fee.EffectiveFrom) <= currentdate,
			);
			setActiveServiceFee(effectiveServiceFees?.[0]);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to retrieve client fees.",
				timeOut: 1500,
			});
		} finally {
			setLoadingServiceFees(false);
		}
	};

	const loadClientHistoricalProcessingFees = async () => {
		try {
			setLoadingProcessingFees(true);
			const response = await ClientService.getProcessingFees(client.Id, { active: false });
			setProcessingFeesHistory(response.data);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to retrieve client fees.",
				timeOut: 1500,
			});
		} finally {
			setLoadingProcessingFees(false);
		}
	};

	const loadClientActiveProcessingFees = async () => {
		try {
			setLoadingProcessingFees(true);
			const response = await ClientService.getProcessingFees(client.Id, { active: true });
			setActiveProcessingFees(response.data);
		} catch (error) {
			utils.notify({
				type: "error",
				message: "Unable to retrieve client active fees.",
				timeOut: 1500,
			});
		} finally {
			setLoadingProcessingFees(false);
		}
	};

	useEffect(() => {
		if (client.Id && show) {
			loadClientServiceFees();
			loadClientActiveProcessingFees();
			loadClientHistoricalProcessingFees();
		}
		if (!show) {
			setServiceFees([]);
			setProcessingFees([]);
			setProcessingFeesHistory([]);
			setActiveProcessingFees([]);
		}
	}, [client, show]);

	useEffect(() => {
		if (activeProcessingFees.length > 0) {
			createProcessingFees();
		}
	}, [activeProcessingFees]);

	const onSaveServiceFee = (data, callback) => {
		const { TypeId, Rate: PercentageFee, EffectiveFrom } = data;
		const payload = { TypeId, PercentageFee, EffectiveFrom };
		ClientService.updateServiceFees(client.Id, payload)
			.then(() => {
				loadClientServiceFees();
				utils.notify({
					type: "success",
					message: "Service fee updated successfully",
					timeout: 1500,
				});
				if (callback) callback();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to update client fees",
					timeout: 1500,
				});
				if (callback) callback(true);
			});
	};
	const filterProcessingFeesHistory = processingFeeId => {
		let filteredTypeFees = processingFeesHistory.filter(
			rate => rate.ProcessingFeeId === processingFeeId,
		);
		// the array is ordered by EffectiveFrom, so we can just filter the first client fee and all the next that are not client fees
		const firstClientFeeIndex = filteredTypeFees.findLastIndex(
			fee => fee.Origin === PROCESSING_FEE_ORIGINS.CLIENT,
		);
		if (firstClientFeeIndex !== -1) {
			filteredTypeFees = filteredTypeFees.filter((fee, idx) => {
				if (idx > firstClientFeeIndex) return true;

				return fee.Origin === PROCESSING_FEE_ORIGINS.CLIENT;
			});
		}
		return filteredTypeFees;
	};

	const filterServiceFeesHistory = () => {
		const sortedFees = [...serviceFees].sort(
			(a, b) => new Date(b.EffectiveFrom) - new Date(a.EffectiveFrom),
		);
		// the array is ordered by EffectiveFrom, so we can just filter the first client fee and all the next that are not client fees
		const firstClientFeeIndex = sortedFees.findLastIndex(fee => fee.ClientId && !fee.IsGlobal);
		if (firstClientFeeIndex !== -1) {
			return sortedFees.filter((fee, idx) => {
				if (idx > firstClientFeeIndex) return true;

				return fee.ClientId;
			});
		}
		return serviceFees;
	};

	const handleShowProcessingFee = processingFeeId => {
		const updateFees = processingFees.map(processingFee => {
			if (processingFee.processingFeeId === processingFeeId) {
				return {
					...processingFee,
					show: !processingFee.show,
				};
			}
			return processingFee;
		});
		setProcessingFees(updateFees);
	};
	const handleShowRKRate = () => {
		setShowRKRate(!showRKRate);
	};

	const onSaveProcessingFee = (data, callback) => {
		const { TypeId: ProcessingFeeId, Rate, EffectiveFrom } = data;

		ClientService.updateProcessingFees(client.Id, {
			ProcessingFeeId,
			Rate,
			EffectiveFrom,
			Modifier: 0,
		})
			.then(() => {
				loadClientActiveProcessingFees();
				loadClientHistoricalProcessingFees();
				utils.notify({
					type: "success",
					message: "Processing fee updated successfully",
					timeout: 1500,
				});
				if (callback) callback();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to update client fees",
					timeout: 1500,
				});
				if (callback) callback(true);
			});
	};

	return (
		<Modal
			id="client-fees-modal"
			show={show}
			onHide={() => onHide()}
		>
			<Modal.Header>
				<Modal.Title>{lang.t("Configure Client Charges")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div style={{ marginLeft: "15px", marginRight: "15px" }}>
					<div className="grouped-service-fees">
						<div className="row v-align service-fee">
							<div className="col-xs-3">
								<Choose>
									<When condition={activeServiceFee || loadingServiceFees}>
										<strong>{lang.t("RKPay Rate")}</strong>
									</When>
									<Otherwise>
										<strong>{lang.t("RKPay rate not found")}</strong>
									</Otherwise>
								</Choose>
							</div>
							<div className="col-xs-7 text-right">
								<If condition={activeServiceFee}>
									<strong>{activeServiceFee?.Rate}%</strong>
								</If>
							</div>
							<div className="col-xs-1">
								<button
									type="button"
									className="btn btn-link padding-left-10"
									onClick={handleShowRKRate}
								>
									<i
										style={{ cursor: "pointer" }}
										className={`h4 rkf ${showRKRate ? "rkf-chevron-up" : "rkf-chevron-down"}`}
									/>
								</button>
							</div>
						</div>
						<If condition={showRKRate}>
							<ModifyRate
								rateHistory={filterServiceFeesHistory()}
								feeTypeId={3}
								onSaveFee={onSaveServiceFee}
								showCreatedBy
							/>
						</If>
					</div>
					<If
						condition={
							type === LAW_ENFORCEMENT_DEPARTMENT && details.NonRKPayPaymentsEnabled
						}
					>
						<For
							each="processingFee"
							of={processingFees}
						>
							<div className="grouped-service-fees">
								<div
									className="row v-align service-fee"
									key={`pFee-${processingFee.id}`}
								>
									<div className="col-xs-3">
										<strong>{processingFee.name}</strong>
									</div>
									<div className="col-xs-7 text-right">
										<strong>{`${processingFee.rate}%`}</strong>
									</div>
									<div className="col-xs-1">
										<button
											type="button"
											className="btn btn-link padding-left-10"
											onClick={() => {
												handleShowProcessingFee(
													processingFee.processingFeeId,
												);
											}}
										>
											<i
												style={{ cursor: "pointer" }}
												className={`h4 rkf ${processingFee.show ? "rkf-chevron-up" : "rkf-chevron-down"}`}
											/>
										</button>
									</div>
								</div>
								<If condition={processingFee.show}>
									<ModifyRate
										rateHistory={filterProcessingFeesHistory(
											processingFee.processingFeeId,
										)}
										feeTypeId={processingFee.processingFeeId}
										onSaveFee={onSaveProcessingFee}
										showCreatedBy
									/>
								</If>
							</div>
						</For>
					</If>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn btn-primary"
					onClick={() => onHide()}
				>
					{lang.t("Close")}
				</button>
			</Modal.Footer>
			<Loader active={loadingProcessingFees || loadingServiceFees} />
		</Modal>
	);
};

ClientFeesModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	client: PropTypes.object.isRequired,
	organizationId: PropTypes.number.isRequired,
	details: PropTypes.object,
	type: PropTypes.number,
};

ClientFeesModal.defaultProps = {
	show: false,
	onHide: () => {},
	details: {},
	type: 0,
};

export default ClientFeesModal;
